import React, { useState, useMemo } from 'react';
import { 
  Bus, Package, Search, RefreshCw, Loader2, 
  ChevronDown, ChevronUp, Users, PoundSterling, Eye,
  Check, X, AlertCircle
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { cn } from '../../../lib/utils';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
  }).format(amount || 0);
};

// Stat card component
const StatCard = ({ title, value, subtitle, icon: Icon, variant = 'default' }) => {
  const variants = {
    default: 'text-slate-900',
    success: 'text-emerald-600',
    warning: 'text-orange-600',
    muted: 'text-slate-500',
  };
  
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-center gap-2 text-slate-500 mb-1">
        {Icon && <Icon className="w-4 h-4" />}
        <span className="text-sm">{title}</span>
      </div>
      <p className={cn("text-xl font-bold", variants[variant])}>{value}</p>
      {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
    </div>
  );
};

// Add-on inventory card
const AddOnCard = ({ addon, bookingsWithAddon }) => {
  const totalGuests = bookingsWithAddon.reduce((sum, b) => {
    const addonData = (b.selected_add_ons || []).find(a => a.name === addon.name);
    if (addonData) {
      // Calculate guests from total_price / price_per_person
      const pricePerPerson = addonData.price_per_person || addonData.price_per_guest || addon.price || 0;
      const totalPrice = addonData.total_price || 0;
      return sum + (pricePerPerson > 0 ? Math.round(totalPrice / pricePerPerson) : 0);
    }
    return sum;
  }, 0);
  
  const totalRevenue = bookingsWithAddon.reduce((sum, b) => {
    const addonData = (b.selected_add_ons || []).find(a => a.name === addon.name);
    return sum + (addonData?.total_price || 0);
  }, 0);
  
  const depositsCollected = bookingsWithAddon.reduce((sum, b) => {
    const addonData = (b.selected_add_ons || []).find(a => a.name === addon.name);
    return sum + (addonData?.total_deposit || 0);
  }, 0);

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
            <Bus className="w-5 h-5 text-violet-600" />
          </div>
          <div>
            <h4 className="font-semibold text-slate-900">{addon.name}</h4>
            {addon.description && (
              <p className="text-xs text-slate-500">{addon.description}</p>
            )}
          </div>
        </div>
        <Badge variant="outline" className="text-violet-600 border-violet-200 bg-violet-50">
          {formatCurrency(addon.price)}/pp
        </Badge>
      </div>
      
      {addon.deposit_amount > 0 && (
        <p className="text-xs text-slate-500 mb-3">
          Deposit: {formatCurrency(addon.deposit_amount)}/pp
        </p>
      )}
      
      <div className="grid grid-cols-3 gap-3 text-center">
        <div className="bg-slate-50 rounded-lg p-2">
          <p className="text-lg font-bold text-slate-900">{bookingsWithAddon.length}</p>
          <p className="text-xs text-slate-500">Bookings</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-2">
          <p className="text-lg font-bold text-slate-900">{totalGuests}</p>
          <p className="text-xs text-slate-500">Guests</p>
        </div>
        <div className="bg-emerald-50 rounded-lg p-2">
          <p className="text-lg font-bold text-emerald-600">{formatCurrency(totalRevenue)}</p>
          <p className="text-xs text-slate-500">Revenue</p>
        </div>
      </div>
    </div>
  );
};

export default function WeekenderAddOns({ 
  event, 
  summaryData,
  bookings,
  loading, 
  authToken,
  onRefresh,
  onBookingSelect
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [addonFilter, setAddonFilter] = useState('all');
  const [sortField, setSortField] = useState('customer');
  const [sortDirection, setSortDirection] = useState('asc');

  // Get configured add-ons from event
  const configuredAddOns = event?.add_ons || [];
  
  // Get bookings with add-ons
  const bookingsWithAddOns = useMemo(() => {
    return (bookings || []).filter(b => 
      b.selected_add_ons && b.selected_add_ons.length > 0 &&
      b.status !== 'cancelled' && b.status !== 'refunded'
    );
  }, [bookings]);

  // Calculate summary stats
  const stats = useMemo(() => {
    const allAddonBookings = bookingsWithAddOns;
    
    let totalRevenue = 0;
    let totalGuests = 0;
    
    allAddonBookings.forEach(b => {
      (b.selected_add_ons || []).forEach(addon => {
        totalRevenue += addon.total_price || 0;
        const pricePerPerson = addon.price_per_person || addon.price_per_guest || 0;
        if (pricePerPerson > 0) {
          totalGuests += Math.round((addon.total_price || 0) / pricePerPerson);
        }
      });
    });
    
    return {
      configuredCount: configuredAddOns.length,
      bookingsWithAddOns: allAddonBookings.length,
      totalGuests,
      totalRevenue
    };
  }, [configuredAddOns, bookingsWithAddOns]);

  // Build flat list of booking + add-on combinations for the table
  const addonBookingRows = useMemo(() => {
    const rows = [];
    
    (bookings || []).forEach(booking => {
      if (booking.status === 'cancelled' || booking.status === 'refunded') return;
      
      (booking.selected_add_ons || []).forEach(addon => {
        const pricePerPerson = addon.price_per_person || addon.price_per_guest || 0;
        const quantity = pricePerPerson > 0 ? Math.round((addon.total_price || 0) / pricePerPerson) : 0;
        
        rows.push({
          booking_id: booking.booking_id,
          booking_ref: booking.booking_id?.slice(0, 15) || '',
          customer_name: booking.customer_name,
          customer_email: booking.customer_email,
          addon_name: addon.name,
          quantity,
          total_price: addon.total_price || 0,
          total_deposit: addon.total_deposit || 0,
          amount_paid: booking.amount_paid || 0,
          total_amount: booking.total_amount || 0,
          booking_status: booking.status,
          booking
        });
      });
    });
    
    return rows;
  }, [bookings]);

  // Filter and sort
  const filteredRows = useMemo(() => {
    let filtered = [...addonBookingRows];
    
    // Search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(row => 
        row.customer_name?.toLowerCase().includes(query) ||
        row.customer_email?.toLowerCase().includes(query) ||
        row.booking_ref?.toLowerCase().includes(query)
      );
    }
    
    // Add-on filter
    if (addonFilter !== 'all') {
      filtered = filtered.filter(row => row.addon_name === addonFilter);
    }
    
    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'customer':
          aVal = a.customer_name || '';
          bVal = b.customer_name || '';
          break;
        case 'addon':
          aVal = a.addon_name || '';
          bVal = b.addon_name || '';
          break;
        case 'quantity':
          aVal = a.quantity || 0;
          bVal = b.quantity || 0;
          break;
        case 'value':
          aVal = a.total_price || 0;
          bVal = b.total_price || 0;
          break;
        default:
          aVal = a.booking_ref;
          bVal = b.booking_ref;
      }
      if (typeof aVal === 'string') {
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    return filtered;
  }, [addonBookingRows, searchQuery, addonFilter, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? 
      <ChevronUp className="w-3 h-3 ml-1" /> : 
      <ChevronDown className="w-3 h-3 ml-1" />;
  };

  const isLoading = loading;

  if (configuredAddOns.length === 0) {
    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Add-ons</h2>
            <p className="text-sm text-slate-500">Event extras and booking add-ons</p>
          </div>
        </div>
        
        <div className="flex flex-col items-center justify-center h-64 text-slate-500 bg-white border border-slate-200 rounded-xl">
          <Package className="w-12 h-12 text-slate-300 mb-3" />
          <p className="font-medium">No Add-ons Configured</p>
          <p className="text-sm text-slate-400 mt-1">
            Add-ons like coach travel can be configured in event settings
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Add-ons</h2>
          <p className="text-sm text-slate-500">Event extras and booking add-ons</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRefresh}
          disabled={isLoading}
          className="text-slate-500 hover:text-slate-700"
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4 mr-1.5" />
          )}
          Refresh
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          title="Configured Add-ons"
          value={stats.configuredCount}
          icon={Package}
        />
        <StatCard
          title="Bookings with Add-ons"
          value={stats.bookingsWithAddOns}
          subtitle={`of ${(bookings || []).filter(b => b.status !== 'cancelled').length} total`}
          icon={Users}
        />
        <StatCard
          title="Guests with Add-ons"
          value={stats.totalGuests}
          icon={Users}
          variant="success"
        />
        <StatCard
          title="Add-on Revenue"
          value={formatCurrency(stats.totalRevenue)}
          icon={PoundSterling}
          variant="success"
        />
      </div>

      {/* Add-on Inventory Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {configuredAddOns.map((addon, idx) => {
          const bookingsForThisAddon = bookingsWithAddOns.filter(b =>
            (b.selected_add_ons || []).some(a => a.name === addon.name)
          );
          return (
            <AddOnCard 
              key={idx} 
              addon={addon} 
              bookingsWithAddon={bookingsForThisAddon}
            />
          );
        })}
      </div>

      {/* Booking Add-ons Table */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Bus className="w-4 h-4 text-violet-600" />
            Booking Add-ons
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Search by name, email, booking ref..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={addonFilter} onValueChange={setAddonFilter}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="Filter by add-on" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Add-ons</SelectItem>
                {configuredAddOns.map((addon, idx) => (
                  <SelectItem key={idx} value={addon.name}>{addon.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Count */}
          <div className="text-sm text-slate-500 mb-3">
            {filteredRows.length} booking add-on{filteredRows.length !== 1 ? 's' : ''}
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
            </div>
          ) : addonBookingRows.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Bus className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No bookings have add-ons yet</p>
              <p className="text-sm mt-1">Customers can add extras when booking or via their booking management page</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-3 font-medium text-slate-600">BOOKING</th>
                      <th 
                        className="text-left py-2 px-3 font-medium text-slate-600 cursor-pointer hover:text-slate-900"
                        onClick={() => handleSort('customer')}
                      >
                        <span className="flex items-center">CUSTOMER <SortIcon field="customer" /></span>
                      </th>
                      <th 
                        className="text-left py-2 px-3 font-medium text-slate-600 cursor-pointer hover:text-slate-900"
                        onClick={() => handleSort('addon')}
                      >
                        <span className="flex items-center">ADD-ON <SortIcon field="addon" /></span>
                      </th>
                      <th 
                        className="text-right py-2 px-3 font-medium text-slate-600 cursor-pointer hover:text-slate-900"
                        onClick={() => handleSort('quantity')}
                      >
                        <span className="flex items-center justify-end">QTY <SortIcon field="quantity" /></span>
                      </th>
                      <th 
                        className="text-right py-2 px-3 font-medium text-slate-600 cursor-pointer hover:text-slate-900"
                        onClick={() => handleSort('value')}
                      >
                        <span className="flex items-center justify-end">VALUE <SortIcon field="value" /></span>
                      </th>
                      <th className="text-left py-2 px-3 font-medium text-slate-600">STATUS</th>
                      <th className="text-right py-2 px-3 font-medium text-slate-600">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-8 text-slate-500">
                          No results match your filters
                        </td>
                      </tr>
                    ) : filteredRows.map((row, idx) => {
                      const isPaid = row.amount_paid >= row.total_amount;
                      const hasDeposit = row.amount_paid > 0;
                      
                      return (
                        <tr 
                          key={`${row.booking_id}-${row.addon_name}-${idx}`} 
                          className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                          onClick={() => onBookingSelect(row.booking)}
                        >
                          <td className="py-3 px-3">
                            <span className="font-mono text-xs text-slate-600">
                              {row.booking_ref}...
                            </span>
                          </td>
                          <td className="py-3 px-3">
                            <div className="font-medium text-slate-900">{row.customer_name}</div>
                            <div className="text-xs text-slate-500">{row.customer_email}</div>
                          </td>
                          <td className="py-3 px-3">
                            <Badge variant="outline" className="text-violet-600 border-violet-200 bg-violet-50">
                              {row.addon_name}
                            </Badge>
                          </td>
                          <td className="py-3 px-3 text-right font-medium text-slate-900">
                            {row.quantity}
                          </td>
                          <td className="py-3 px-3 text-right font-medium text-slate-900">
                            {formatCurrency(row.total_price)}
                          </td>
                          <td className="py-3 px-3">
                            <Badge className={cn(
                              "text-xs",
                              isPaid 
                                ? "bg-emerald-100 text-emerald-700" 
                                : hasDeposit 
                                  ? "bg-blue-100 text-blue-700"
                                  : "bg-yellow-100 text-yellow-700"
                            )}>
                              {isPaid ? 'Paid' : hasDeposit ? 'Deposit' : 'Pending'}
                            </Badge>
                          </td>
                          <td className="py-3 px-3 text-right">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                onBookingSelect(row.booking);
                              }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden space-y-3">
                {filteredRows.map((row, idx) => {
                  const isPaid = row.amount_paid >= row.total_amount;
                  
                  return (
                    <div 
                      key={`${row.booking_id}-${row.addon_name}-${idx}`}
                      className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 cursor-pointer"
                      onClick={() => onBookingSelect(row.booking)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium text-slate-900">{row.customer_name}</p>
                          <p className="text-xs text-slate-500 font-mono">{row.booking_ref}...</p>
                        </div>
                        <Badge className={cn(
                          "text-xs",
                          isPaid ? "bg-emerald-100 text-emerald-700" : "bg-yellow-100 text-yellow-700"
                        )}>
                          {isPaid ? 'Paid' : 'Pending'}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className="text-violet-600 border-violet-200 bg-violet-50">
                          {row.addon_name} x{row.quantity}
                        </Badge>
                        <span className="font-semibold text-slate-900">
                          {formatCurrency(row.total_price)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
